import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Filter,
  RefreshCw,
  Search,
  ShieldCheck,
  User,
  X
} from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";

interface AuditLog {
  id: number;
  user_id: number | null;
  user_email: string | null;
  user_role: string | null;
  action: string;
  entity_type: string;
  entity_id: number | null;
  entity_name: string | null;
  tenant_id: number | null;
  tenant_name: string | null;
  subdomain: string | null;
  changes: any;
  created_at: string;
}

interface AuditStatsResponse {
  dailyStats?: Array<{
    total_actions?: number;
    unique_users?: number;
    unique_tenants?: number;
    unique_actions?: number;
    date?: string;
    actions_count?: number;
  }>;
  topActions?: Array<{ action: string; count: number }>;
  topUsers?: Array<{ user_email: string; user_role: string; action_count: number }>;
}

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "CREATE_SCHOOL", label: "Create School" },
  { value: "UPDATE_SCHOOL", label: "Update School" },
  { value: "SUSPEND_SCHOOL", label: "Suspend School" },
  { value: "CREATE_SUBSCRIPTION", label: "Create Subscription" },
  { value: "UPDATE_SUBSCRIPTION", label: "Update Subscription" },
  { value: "CREATE_USER", label: "Create User" },
  { value: "DELETE_USER", label: "Delete User" },
  { value: "LOGIN", label: "User Login" }
];

const ENTITY_OPTIONS = [
  { value: "", label: "All entities" },
  { value: "school", label: "School" },
  { value: "subscription", label: "Subscription" },
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
  { value: "headteacher", label: "Headteacher" },
  { value: "global_subject", label: "Global Subject" },
  { value: "global_class", label: "Global Class" }
];

const prettyJson = (value: any) => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
};

const AuditTrail: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [stats, setStats] = useState<AuditStatsResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [filters, setFilters] = useState({
    action: "",
    entity_type: "",
    tenant_id: "",
    start_date: "",
    end_date: ""
  });

  const [schools, setSchools] = useState<any[]>([]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchSchools = async () => {
    try {
      const res = await axios.get(getApiUrl("superadmin/schools"), { headers });
      setSchools(res.data || []);
    } catch {
      // keep page usable even if school list fails
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(getApiUrl("superadmin/audit-trail/stats"), { headers });
      setStats(res.data || {});
    } catch {
      setStats(null);
    }
  };

  const fetchLogs = async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: "20",
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== ""))
      });

      const response = await axios.get(getApiUrl(`superadmin/audit-trail?${queryParams}`), { headers });
      setLogs(response.data.logs || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setTotal(response.data.pagination?.total || 0);
    } catch {
      setError("Failed to load audit logs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSchools();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, filters]);

  const filteredLogs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return logs;
    return logs.filter((log) =>
      [
        log.user_email,
        log.user_role,
        log.action,
        log.entity_type,
        log.entity_name,
        log.tenant_name,
        log.subdomain
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [logs, searchTerm]);

  const summary = useMemo(() => {
    const topDay = stats?.dailyStats?.[0];
    return {
      totalActions: topDay?.total_actions || total,
      uniqueUsers: topDay?.unique_users || 0,
      uniqueTenants: topDay?.unique_tenants || 0,
      uniqueActions: topDay?.unique_actions || 0
    };
  }, [stats, total]);

  const getActionColor = (action: string) => {
    if (action.includes("CREATE")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (action.includes("DELETE") || action.includes("SUSPEND")) return "bg-rose-50 text-rose-700 border-rose-200";
    if (action.includes("UPDATE")) return "bg-blue-50 text-blue-700 border-blue-200";
    if (action.includes("LOGIN")) return "bg-indigo-50 text-indigo-700 border-indigo-200";
    return "bg-slate-50 text-slate-600 border-slate-200";
  };

  const resetFilters = () => {
    setFilters({ action: "", entity_type: "", tenant_id: "", start_date: "", end_date: "" });
    setSearchTerm("");
    setPage(1);
  };

  const exportCsv = () => {
    const rows = filteredLogs.map((log) => ({
      timestamp: new Date(log.created_at).toISOString(),
      user_email: log.user_email || "System",
      user_role: log.user_role || "",
      action: log.action,
      entity_type: log.entity_type,
      entity_name: log.entity_name || "",
      tenant_name: log.tenant_name || "Platform Hub",
      entity_id: log.entity_id || "",
      changes: prettyJson(log.changes)
    }));

    const header = Object.keys(rows[0] || {
      timestamp: "",
      user_email: "",
      user_role: "",
      action: "",
      entity_type: "",
      entity_name: "",
      tenant_name: "",
      entity_id: "",
      changes: ""
    });
    const csv = [
      header.join(","),
      ...rows.map((row) =>
        header
          .map((key) => `"${String((row as any)[key] ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-trail-page-${page}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  if (loading && page === 1) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50/30">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900" />
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Scanning security logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main bg-slate-50/20 p-8 text-sm">
        <header className="mb-8 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                System Sovereignty
              </span>
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900">
              Audit <span className="italic text-slate-500">Explorer</span>
            </h1>
            <p className="mt-2 font-medium tracking-tight text-slate-500">
              Investigating {total} platform-wide security events across the ecosystem.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                fetchStats();
                fetchLogs(true);
              }}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition-all hover:bg-slate-50"
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={exportCsv}
              className="flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-slate-800 shadow-lg shadow-slate-200"
            >
              <Download size={18} /> Export CSV
            </button>
          </div>
        </header>

        {error ? (
          <div className="mb-8 flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700">
            <div className="h-2 w-2 animate-pulse rounded-full bg-rose-500" />
            {error}
            <button onClick={() => setError("")} className="ml-auto text-rose-400 hover:text-rose-600">
              <X size={16} />
            </button>
          </div>
        ) : null}

        <section className="mb-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[
            { label: "Total Actions", value: summary.totalActions, Icon: Activity, tone: "bg-slate-900 text-white border-slate-900" },
            { label: "Active Operators", value: summary.uniqueUsers, Icon: User, tone: "bg-blue-50 text-blue-700 border-blue-200" },
            { label: "Schools Touched", value: summary.uniqueTenants, Icon: Building2, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
            { label: "Action Types", value: summary.uniqueActions, Icon: ShieldCheck, tone: "bg-violet-50 text-violet-700 border-violet-200" }
          ].map(({ label, value, Icon, tone }) => (
            <article key={label} className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-xl shadow-slate-200/40">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{label}</p>
                  <p className="mt-3 text-3xl font-black tracking-tight text-slate-900">{value}</p>
                </div>
                <div className={`rounded-2xl border p-3 ${tone}`}>
                  <Icon size={18} />
                </div>
              </div>
            </article>
          ))}
        </section>

        <div className="mb-10 overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-xl shadow-slate-200/50">
          <div className="grid grid-cols-1 gap-4 border-b border-slate-50 bg-slate-50/50 p-6 md:grid-cols-2 xl:grid-cols-6">
            <div className="relative group xl:col-span-2">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-slate-900" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search actor, action, entity, school"
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm font-medium outline-none transition-all focus:ring-4 focus:ring-slate-900/5"
              />
            </div>

            <div className="relative group">
              <Activity className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-slate-900" />
              <select
                value={filters.action}
                onChange={(e) => {
                  setFilters({ ...filters, action: e.target.value });
                  setPage(1);
                }}
                className="w-full appearance-none rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-[11px] font-bold uppercase tracking-wider outline-none transition-all focus:ring-4 focus:ring-slate-900/5"
              >
                {ACTION_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative group">
              <Filter className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-slate-900" />
              <select
                value={filters.entity_type}
                onChange={(e) => {
                  setFilters({ ...filters, entity_type: e.target.value });
                  setPage(1);
                }}
                className="w-full appearance-none rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-[11px] font-bold uppercase tracking-wider outline-none transition-all focus:ring-4 focus:ring-slate-900/5"
              >
                {ENTITY_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative group">
              <Building2 className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors group-focus-within:text-slate-900" />
              <select
                value={filters.tenant_id}
                onChange={(e) => {
                  setFilters({ ...filters, tenant_id: e.target.value });
                  setPage(1);
                }}
                className="w-full appearance-none rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-[11px] font-bold uppercase tracking-wider outline-none transition-all focus:ring-4 focus:ring-slate-900/5"
              >
                <option value="">All schools</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative group">
              <Calendar className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => {
                  setFilters({ ...filters, start_date: e.target.value });
                  setPage(1);
                }}
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-[11px] font-bold outline-none transition-all focus:ring-4 focus:ring-slate-900/5"
              />
            </div>

            <div className="relative group">
              <Calendar className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => {
                  setFilters({ ...filters, end_date: e.target.value });
                  setPage(1);
                }}
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-[11px] font-bold outline-none transition-all focus:ring-4 focus:ring-slate-900/5"
              />
            </div>
          </div>

          <div className="border-b border-slate-50 px-6 py-4">
            <button
              onClick={resetFilters}
              className="rounded-2xl bg-slate-100 px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest text-slate-500 transition-all hover:bg-slate-200"
            >
              Reset Filters
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50/10">
                <tr>
                  <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">Timestamp</th>
                  <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">Actor</th>
                  <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Action</th>
                  <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">Environment</th>
                  <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400">Target</th>
                  <th className="px-8 py-5 text-[11px] font-bold uppercase tracking-widest text-slate-400 text-right">Inspect</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-8 py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex h-16 w-16 items-center justify-center rounded-[2rem] bg-slate-50">
                          <ShieldCheck size={28} className="text-slate-200" />
                        </div>
                        <p className="text-sm font-bold uppercase tracking-widest text-slate-300">
                          Protocol secure - No anomalies matching filters
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="group font-medium transition-all hover:bg-slate-50/50">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-50 text-slate-300">
                            <Clock size={12} />
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-900">{new Date(log.created_at).toLocaleDateString()}</div>
                            <div className="text-[10px] text-slate-400">{new Date(log.created_at).toLocaleTimeString()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-[10px] font-bold text-indigo-500">
                            {log.user_email ? log.user_email[0].toUpperCase() : "?"}
                          </div>
                          <div>
                            <div className="text-xs font-bold tracking-tight text-slate-800">{log.user_email || "System"}</div>
                            <div className="text-[10px] font-bold uppercase tracking-tighter text-teal-600">{log.user_role || "system"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${getActionColor(log.action)}`}>
                          {log.action.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-slate-200" />
                          <span className="text-xs font-bold italic tracking-tight text-slate-600">{log.tenant_name || "Platform Hub"}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div>
                          <div className="text-xs font-bold text-slate-800">{log.entity_name || "Resource"}</div>
                          <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            {log.entity_type} • ID: {log.entity_id || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <button
                          type="button"
                          onClick={() => setSelectedLog(log)}
                          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50"
                        >
                          View changes
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-slate-50 bg-slate-50/30 p-6">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
              Page {page} of {totalPages} <span className="mx-2 text-slate-200">|</span> Total Events: {total}
            </div>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-400 shadow-sm transition-all hover:text-slate-900 disabled:opacity-30"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-400 shadow-sm transition-all hover:text-slate-900 disabled:opacity-30"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>

        <section className="grid gap-6 xl:grid-cols-2">
          <article className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-xl shadow-slate-200/40">
            <h2 className="text-xl font-bold text-slate-900">Top Actions (30 days)</h2>
            <div className="mt-5 space-y-3">
              {(stats?.topActions || []).length ? (
                (stats?.topActions || []).map((item) => (
                  <div key={item.action} className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <span className="text-sm font-semibold text-slate-700">{item.action.replace(/_/g, " ")}</span>
                    <span className="text-sm font-black text-slate-900">{item.count}</span>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  No action telemetry available yet.
                </div>
              )}
            </div>
          </article>

          <article className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-xl shadow-slate-200/40">
            <h2 className="text-xl font-bold text-slate-900">Most Active Operators (30 days)</h2>
            <div className="mt-5 space-y-3">
              {(stats?.topUsers || []).length ? (
                (stats?.topUsers || []).map((item) => (
                  <div key={`${item.user_email}-${item.user_role}`} className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-semibold text-slate-800">{item.user_email}</p>
                        <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-slate-400">{item.user_role}</p>
                      </div>
                      <span className="text-sm font-black text-slate-900">{item.action_count}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  No operator activity available yet.
                </div>
              )}
            </div>
          </article>
        </section>
      </main>

      {selectedLog ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Audit Detail</p>
                <h2 className="mt-2 text-2xl font-black text-slate-900">{selectedLog.action.replace(/_/g, " ")}</h2>
                <p className="mt-2 text-sm text-slate-500">
                  {selectedLog.user_email || "System"} • {new Date(selectedLog.created_at).toLocaleString()}
                </p>
              </div>
              <button onClick={() => setSelectedLog(null)} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
                <X size={18} />
              </button>
            </div>
            <div className="grid gap-6 overflow-y-auto p-6 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Actor</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{selectedLog.user_email || "System"}</p>
                  <p className="mt-1 text-xs uppercase tracking-widest text-slate-500">{selectedLog.user_role || "system"}</p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Target</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{selectedLog.entity_name || "Resource"}</p>
                  <p className="mt-1 text-xs uppercase tracking-widest text-slate-500">
                    {selectedLog.entity_type} • ID: {selectedLog.entity_id || "N/A"}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Environment</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{selectedLog.tenant_name || "Platform Hub"}</p>
                  <p className="mt-1 text-xs uppercase tracking-widest text-slate-500">{selectedLog.subdomain || "global"}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Changes Payload</p>
                <pre className="mt-3 overflow-x-auto rounded-2xl border border-slate-100 bg-slate-950 p-4 text-xs leading-6 text-slate-100">
                  {prettyJson(selectedLog.changes)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AuditTrail;
